import AttachmentsForSmimeResponse from '../store/schema/AttachmentsForSmimeResponse';
import SmimeAttachmentType from 'owa-attachment-model-store/lib/store/schema/SmimeAttachmentType';
import TypeOfAttachment from 'owa-attachment-model-store/lib/store/schema/TypeOfAttachment';
import getTypeOfAttachment from 'owa-attachment-model-store/lib/utils/getTypeOfAttachment';
import isSmimeAttachmentType from 'owa-attachment-model-store/lib/utils/isSmimeAttachmentType';
import AttachmentId from 'owa-service/lib/contract/AttachmentId';
import AttachmentType from 'owa-service/lib/contract/AttachmentType';
import ItemId from 'owa-service/lib/contract/ItemId';
import attachmentType from 'owa-service/lib/factory/attachmentType';

export const cloneAttachment = (
    attachment: AttachmentType,
    parentItemId: ItemId
): AttachmentType => {
    if (!attachment) {
        return attachment;
    }

    const clonedAttachment: AttachmentType = attachmentType({
        __type: attachment.__type,
        AttachmentId: {
            ...attachment.AttachmentId,
            RootItemId: parentItemId?.Id,
        } as AttachmentId,
        Name: attachment.Name,
        ContentType: attachment.ContentType,
        ContentId: attachment.ContentId,
        ContentLocation: attachment.ContentLocation,
        AttachmentOriginalUrl: attachment.AttachmentOriginalUrl,
        Size: attachment.Size,
        LastModifiedTime: attachment.LastModifiedTime,
        IsInline: attachment.IsInline,
        IsInlineToUniqueBody: attachment.IsInlineToUniqueBody,
        IsInlineToNormalBody: attachment.IsInlineToNormalBody,
        ThumbnailMimeType: attachment.ThumbnailMimeType,
        Thumbnail: attachment.Thumbnail,
    });

    if (isSmimeAttachmentType(attachment)) {
        (<SmimeAttachmentType>clonedAttachment).IsSmimeDecoded = attachment.IsSmimeDecoded;
        (<SmimeAttachmentType>clonedAttachment).Content = attachment.Content;
        (<SmimeAttachmentType>clonedAttachment).MimeContent = attachment.MimeContent;
    }

    return clonedAttachment;
};

/**
 * Returns attachments that will be downloaded and cloned for a S/MIME response, depending on the mail response type,
 * and the names of the attachments that were not because they are not supported in S/MIME
 * @param parentAttachments attachments which need to be cloned in the mail response item
 * @param parentItemId itemId of the parent item
 * @param isForward flag denoting if this is a forward scenario
 */
const getAttachmentsForSmimeResponse = (
    parentAttachments: AttachmentType[],
    parentItemId: ItemId,
    isForward?: boolean
): AttachmentsForSmimeResponse => {
    let supportedAttachments: AttachmentType[];
    let unsupportedAttachmentNames: string[];

    if (parentAttachments) {
        supportedAttachments = [];
        unsupportedAttachmentNames = [];

        // Since these attachments are part of the parent mail item, the RootItemId should be set appropriately
        // Also we need to clone these attachments to avoid parent attachment from mutation.
        for (const attachment of parentAttachments) {
            if (
                isForward ||
                attachment.IsInline /*Only inline attachments are needed when reply/reply all.*/
            ) {
                const attachmentType = getTypeOfAttachment(attachment);
                if (attachmentType === TypeOfAttachment.Reference) {
                    // Filter out cloud or URI attachments, which are not supported in S/MIME
                    // Save file names to display on an error info bar (VSO 55289)
                    unsupportedAttachmentNames.push(attachment.Name);
                } else {
                    supportedAttachments.push(cloneAttachment(attachment, parentItemId));
                }
            }
        }
    }

    const attachments: AttachmentsForSmimeResponse = {
        supportedSmimeAttachments: supportedAttachments,
        unsupportedSmimeAttachmentNames: unsupportedAttachmentNames,
    };
    return attachments;
};

export default getAttachmentsForSmimeResponse;
